import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { get, uniqBy } from 'lodash-es';
import classnames from 'classnames';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { AppraisalEntities, AppraisalModel, buildAllStylesAppraisalFilteredPath } from 'client/data/models/appraisal';
import { buildFuelCostsPath, FeatureSpecsEntities, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { FEATURE_SPEC_TOOLTIP, FEATURE_SPEC_TOOLTIP_MAP } from 'site-modules/shared/constants/features-specs';
import { capitalizeEachWord } from 'client/utils/string-utils';
import {
  formatStyleDetails,
  getFeatureValue,
  getUsedTmvRetail,
  isAvailable,
} from 'site-modules/shared/utils/features-specs/utils';
import { getPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { numberWithCommasForString } from 'site-modules/shared/utils/string';
import { CoreIncentivesWizard } from 'site-modules/core-page/components/core-incentives-wizard/core-incentives-wizard';
import { PopoverItem } from 'site-modules/shared/components/popover-item/popover-item';
import { Select } from 'site-modules/shared/components/select/select';

const DRIVETRAIN_SELECT = 'features-drivetrain';

function FeaturesListUI({
  vehicleParams,
  className = null,
  styleId,
  stylesFeatures = undefined,
  styleEvTestedData = undefined,
  fuelCostsData = null,
  appraisalStyles = null,
  onStyleChange,
  isUsed = false,
  isMobile = false,
}) {
  const { makeName, modelName, makeSlug, modelSlug, year } = vehicleParams;
  const styleFeatures = stylesFeatures.find(({ id }) => id === styleId);
  const {
    baseMSRP,
    mpg,
    totalSeating,
    driveType,
    engineType,
    basicWarranty,
    measurements: { cargoAllSeats },
    electric: { mpge, isElectric, isPluginElectric, timeToCharge, eRange, batteryWarranty },
    truck: { isTruck, maxTowingCapacity },
  } = formatStyleDetails(styleFeatures);
  const costPerMonth = get(fuelCostsData, 'fuelCosts.costPerMonth', 0);
  const evTestedRange = get(styleEvTestedData, 'range');
  const usedTmvRetail = getUsedTmvRetail(appraisalStyles, styleId);

  const driveTrainOptions = useMemo(
    () =>
      uniqBy(
        stylesFeatures
          .filter(({ trim }) => trim === styleFeatures.trim)
          .map(({ id, features }) => ({
            id,
            driveTrain: capitalizeEachWord(getFeatureValue(features, 'standard["Drive Train"]', 'Drive type')),
          })),
        'driveTrain'
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [styleFeatures.trim]
  );

  const featuresMap = [
    {
      key: 'Base MSRP',
      label: (
        <Fragment>
          Base MSRP
          <div className="small">Excludes Destination Fee</div>
        </Fragment>
      ),
      show: !isUsed && !!baseMSRP,
      datapoints: [baseMSRP],
      value: baseMSRP,
    },
    {
      label: 'Est. Market Value',
      show: isUsed && !!usedTmvRetail,
      datapoints: [usedTmvRetail],
      value: getPriceStringV2(usedTmvRetail),
    },
    {
      label: 'EV Tax Credits & Rebates',
      show: !!styleFeatures?.styleAttributes?.electric || !!styleFeatures?.styleAttributes?.pluginElectric,
      datapoints: [],
      value: (
        <CoreIncentivesWizard
          styleId={styleId}
          makeName={makeName}
          modelName={modelName}
          makeSlug={makeSlug}
          modelSlug={modelSlug}
          year={year}
          engineType={engineType}
          isUsed={isUsed}
          isMobile={isMobile}
        />
      ),
    },
    {
      label: 'Engine Type',
      show: true,
      datapoints: [engineType],
      value: engineType,
    },
    {
      label: 'Edmunds Tested Electric Range',
      show: !!evTestedRange,
      datapoints: [evTestedRange],
      valueClassName: 'd-flex flex-column justify-content-center align-items-end',
      value: (
        <Fragment>
          {evTestedRange} miles
          {isAvailable(eRange) && <div className="small">vs EPA Range {eRange.replace('mi.', 'miles')}</div>}
        </Fragment>
      ),
    },
    {
      label: 'EPA Electric Range',
      show: !evTestedRange && (isElectric || isPluginElectric),
      datapoints: [eRange],
      value: `${eRange.replace('mi.', 'miles')}`,
    },
    {
      label: 'Combined MPG',
      show: !isElectric && !isPluginElectric,
      datapoints: [mpg.combined],
      value: `${mpg.combined} MPG`,
    },
    {
      key: 'Combined MPGe',
      label: (
        <Fragment>
          Combined MPGe <div className="small">(Electric + Gas)</div>
        </Fragment>
      ),
      show: isPluginElectric,
      datapoints: [mpge.combined],
      value: (
        <Fragment>
          {mpge.combined} MPGe
          <PopoverItem
            placement="top"
            openTrackingParams={{ creative_id: 'features-and-specs', value: 'MPGe' }}
            screenReaderTooltipLabel="MPGe"
          >
            {FEATURE_SPEC_TOOLTIP[FEATURE_SPEC_TOOLTIP_MAP.MPGE_FULL]}
          </PopoverItem>
        </Fragment>
      ),
    },
    {
      label: 'Cost to Drive',
      show: true,
      datapoints: [costPerMonth],
      value: `${getPriceStringV2(costPerMonth)}/month`,
    },
    {
      label: 'Total Charging Time (240V)',
      show: isElectric || isPluginElectric,
      datapoints: [timeToCharge],
      value: `${timeToCharge.replace('hr.', 'hours')}`,
    },
    {
      label: 'Seating',
      show: true,
      datapoints: [totalSeating],
      value: `${totalSeating} seats`,
    },
    {
      key: 'Cargo Capacity',
      label: (
        <Fragment>
          Cargo Capacity <div className="small">All Seats In Place</div>
        </Fragment>
      ),
      show: true,
      datapoints: [cargoAllSeats],
      value: cargoAllSeats,
    },
    {
      key: 'Drivetrain',
      label:
        driveTrainOptions.length > 1 ? (
          <label className="mb-0" htmlFor={DRIVETRAIN_SELECT}>
            Drivetrain
          </label>
        ) : (
          'Drivetrain'
        ),
      show: true,
      datapoints: [driveType],
      valueClassName: classnames('text-capitalize', {
        'd-flex justify-content-end align-items-start': driveTrainOptions.length > 1,
      }),
      value:
        driveTrainOptions.length > 1 ? (
          <div className="drivetrain-select pos-r">
            <Select
              id={DRIVETRAIN_SELECT}
              name={DRIVETRAIN_SELECT}
              inputClassName="border-0 pos-a top-0 left-0 w-100 h-100"
              options={driveTrainOptions}
              value={capitalizeEachWord(driveType)}
              valueKey="driveTrain"
              labelKey="driveTrain"
              aria-label="Select a drivetrain"
              onChange={onStyleChange}
              data-tracking-id="drivetrain_select"
            />
            <div className="pos-r select-overlay rounded bg-white text-primary-darker fw-normal">
              {driveType} <i className="icon-arrow-down3 size-10" aria-hidden />
            </div>
          </div>
        ) : (
          driveType
        ),
    },
    {
      label: 'Max Towing Capacity',
      show: isTruck,
      datapoints: [maxTowingCapacity],
      value: maxTowingCapacity,
    },
    {
      label: 'Warranty',
      show: true,
      datapoints: [basicWarranty],
      value: numberWithCommasForString(basicWarranty.replace('yr.', 'years ').replace('mi.', 'miles')),
    },
    {
      label: 'EV Battery Warranty',
      show: isElectric || isPluginElectric,
      datapoints: [batteryWarranty],
      value: numberWithCommasForString(batteryWarranty.replace('yr.', 'years ').replace('mi.', 'miles')),
    },
  ].filter(({ show, datapoints, value }) => show && datapoints.every(isAvailable) && value);

  const breakIndex = Math.ceil(featuresMap.length / 2);

  return (
    <table className={classnames('features-list w-100', className)}>
      <tbody
        style={{
          '--features-break-index': breakIndex,
        }}
      >
        {featuresMap.map(({ key, label, value, valueClassName }, index) => (
          <tr
            key={key || label}
            className={classnames('h-100 d-flex px-md-1', {
              'border-end': !isMobile && index < breakIndex,
              'border-bottom': (isMobile || index + 1 !== breakIndex) && index + 1 !== featuresMap?.length,
            })}
          >
            <th scope="row" className="col-auto fw-normal py-1" style={{ maxWidth: '60%' }}>
              {label}
            </th>
            <td className={classnames('col fw-bold py-1', valueClassName)} align="right">
              {value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

FeaturesListUI.propTypes = {
  vehicleParams: PropTypes.shape({
    makeSlug: PropTypes.string,
    modelSlug: PropTypes.string,
    makeName: PropTypes.string,
    modelName: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  styleId: PropTypes.number.isRequired,
  onStyleChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  stylesFeatures: PropTypes.arrayOf(FeatureSpecsEntities.FeatureStyleEntity),
  styleEvTestedData: VehicleEntities.StyleEvTestedData,
  fuelCostsData: VehicleEntities.StyleFuelCostData,
  appraisalStyles: AppraisalEntities.Styles,
  isUsed: PropTypes.bool,
  isMobile: PropTypes.bool,
};

export const propsAreEqual = (prevProps, { appraisalStyles, fuelCostsData, isUsed }) =>
  !fuelCostsData && prevProps?.appraisalStyles === appraisalStyles && prevProps?.isUsed === isUsed;
export const FeaturesList = connectToModel(React.memo(FeaturesListUI, propsAreEqual), {
  appraisalStyles: bindToPath(
    ({ isUsed, vehicleParams }) =>
      isUsed &&
      buildAllStylesAppraisalFilteredPath({
        make: vehicleParams.makeSlug,
        model: vehicleParams.modelSlug,
        year: vehicleParams.year,
      }),
    AppraisalModel
  ),
  fuelCostsData: bindToPath(({ styleId }) => buildFuelCostsPath(styleId), VehicleModel),
});
